// MusicXML export — the format Noteflight imports without re-guessing anything.
// Unlike MIDI, this writes the bar lines, the rest values and the key signature
// explicitly, so the score opens reading exactly the way the staff reads here.
import { EIGHTHS_PER_BAR } from "./measures";

const FLAT_ORDER = ["B", "E", "A", "D", "G", "C", "F"];
const SHARP_ORDER = ["F", "C", "G", "D", "A", "E", "B"];
// Two divisions per quarter note, so an eighth is 1 and a whole bar is 8.
const DIVISIONS = 2;
const TYPE_BY_EIGHTHS = { 1: "eighth", 2: "quarter", 3: "quarter", 4: "half", 6: "half", 8: "whole" };
const ACCIDENTAL_NAME = { "#": "sharp", b: "flat" };

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Same loose rule the staff draws with, so the printed key signature matches.
function keySignature(notes) {
  const seen = new Map();
  notes.forEach((n) => {
    if (!n.note) return;
    const acc = n.accidental || null;
    if (!seen.has(n.note)) seen.set(n.note, acc);
    else if (seen.get(n.note) !== acc) seen.set(n.note, "mixed");
  });
  const flats = FLAT_ORDER.filter((n) => seen.get(n) === "b");
  const sharps = SHARP_ORDER.filter((n) => seen.get(n) === "#");
  if (flats.length && sharps.length) return { fifths: 0, letters: new Set() };
  // A real key signature only exists if the flats (or sharps) are the standard
  // run — B♭ E♭ A♭ … Scales like Ahavah Rabbah often aren't, and then every
  // accidental gets printed on the note instead.
  const isRun = (list, order) =>
    list.length > 0 && list.every((n, i) => order[i] === n);
  if (isRun(flats, FLAT_ORDER)) return { fifths: -flats.length, letters: new Set(flats) };
  if (isRun(sharps, SHARP_ORDER)) return { fifths: sharps.length, letters: new Set(sharps) };
  return { fifths: 0, letters: new Set() };
}

// Low letters live in the bass clef, the rest in the treble — the same split
// the grand staff uses on screen.
const staffFor = (entry) => (entry.octave >= 4 ? 1 : 2);

function noteXml(entry, eighths, sigLetters) {
  const alter = entry.accidental === "#" ? 1 : entry.accidental === "b" ? -1 : 0;
  const type = TYPE_BY_EIGHTHS[eighths] || "eighth";
  const dot = eighths === 3 || eighths === 6 ? "        <dot/>\n" : "";
  const accidental =
    entry.accidental && !sigLetters.has(entry.note)
      ? `        <accidental>${ACCIDENTAL_NAME[entry.accidental]}</accidental>\n`
      : "";
  const lyric = entry.letter
    ? `        <lyric number="1"><syllabic>single</syllabic><text>${esc(entry.letter)}</text></lyric>\n`
    : "";
  return (
    `      <note>\n` +
    `        <pitch><step>${entry.note}</step>${alter ? `<alter>${alter}</alter>` : ""}<octave>${entry.octave}</octave></pitch>\n` +
    `        <duration>${eighths}</duration>\n` +
    `        <voice>1</voice>\n` +
    `        <type>${type}</type>\n` +
    dot +
    accidental +
    `        <staff>${staffFor(entry)}</staff>\n` +
    lyric +
    `      </note>\n`
  );
}

function restXml(eighths) {
  const type = TYPE_BY_EIGHTHS[eighths] || "eighth";
  return (
    `      <note>\n` +
    `        <rest/>\n` +
    `        <duration>${eighths}</duration>\n` +
    `        <voice>1</voice>\n` +
    `        <type>${type}</type>\n` +
    `        <staff>1</staff>\n` +
    `      </note>\n`
  );
}

/**
 * Build a MusicXML document for what is on the staff.
 * @param steps  measured steps (with rest / restStart / restEighths) or, when
 *               `measured` is false, the plain note sequence.
 * @param stepMs milliseconds per step, so the tempo marking matches playback.
 */
export function buildMusicXml({ steps, stepMs, measured, title, credit }) {
  const notes = steps.filter((s) => !s.rest);
  if (!notes.length) return null;
  const sig = keySignature(notes);
  // In measured mode a step is an eighth; otherwise each letter is a quarter.
  const eighthsPerStep = measured ? 1 : 2;
  const quarterBpm = Math.max(
    20,
    Math.round(60000 / (stepMs * (measured ? 2 : 1)))
  );

  // Lay the steps out into bars of 8 eighths.
  const measuresOut = [];
  let current = [];
  let filled = 0;
  const flush = () => {
    if (current.length) measuresOut.push(current);
    current = [];
    filled = 0;
  };
  steps.forEach((step, i) => {
    if (step.rest && step.restStart === false) return; // covered by its group
    const eighths = step.rest
      ? step.restEighths || 1
      : eighthsPerStep;
    if (filled + eighths > EIGHTHS_PER_BAR) flush();
    current.push({ step, eighths });
    filled += eighths;
    if (filled >= EIGHTHS_PER_BAR) flush();
    if (i === steps.length - 1) {
      // Pad the final bar so it closes on a bar line.
      if (current.length && filled < EIGHTHS_PER_BAR) {
        current.push({ step: { rest: true }, eighths: EIGHTHS_PER_BAR - filled });
      }
      flush();
    }
  });

  const body = measuresOut
    .map((bar, idx) => {
      const head =
        idx === 0
          ? `      <attributes>\n` +
            `        <divisions>${DIVISIONS}</divisions>\n` +
            `        <key><fifths>${sig.fifths}</fifths></key>\n` +
            `        <time><beats>4</beats><beat-type>4</beat-type></time>\n` +
            `        <staves>2</staves>\n` +
            `        <clef number="1"><sign>G</sign><line>2</line></clef>\n` +
            `        <clef number="2"><sign>F</sign><line>4</line></clef>\n` +
            `      </attributes>\n` +
            `      <direction placement="above">\n` +
            `        <direction-type><metronome><beat-unit>quarter</beat-unit><per-minute>${quarterBpm}</per-minute></metronome></direction-type>\n` +
            `        <sound tempo="${quarterBpm}"/>\n` +
            `      </direction>\n`
          : "";
      const content = bar
        .map(({ step, eighths }) =>
          step.rest ? restXml(eighths) : noteXml(step, eighths, sig.letters)
        )
        .join("");
      const last =
        idx === measuresOut.length - 1
          ? `      <barline location="right"><bar-style>light-heavy</bar-style></barline>\n`
          : "";
      return `    <measure number="${idx + 1}">\n${head}${content}${last}    </measure>\n`;
    })
    .join("");

  return (
    `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 4.0 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">\n` +
    `<score-partwise version="4.0">\n` +
    `  <work><work-title>${esc(title || "Alephonics")}</work-title></work>\n` +
    `  <identification>\n` +
    `    <creator type="composer">${esc(credit || "Alephonics by Dan Berry · jewishmusiclab.com")}</creator>\n` +
    `    <encoding><software>Alephonics</software><encoding-date>${new Date().toISOString().slice(0, 10)}</encoding-date></encoding>\n` +
    `  </identification>\n` +
    `  <part-list>\n` +
    `    <score-part id="P1">\n` +
    `      <part-name>Alephonics</part-name>\n` +
    `      <score-instrument id="P1-I1"><instrument-name>Piano</instrument-name></score-instrument>\n` +
    `      <midi-instrument id="P1-I1"><midi-channel>1</midi-channel><midi-program>1</midi-program></midi-instrument>\n` +
    `    </score-part>\n` +
    `  </part-list>\n` +
    `  <part id="P1">\n${body}  </part>\n` +
    `</score-partwise>\n`
  );
}

// Noteflight's Launch API: it fetches the file from this URL client-side, so the
// URL has to be public and CORS-open.
export function noteflightLaunchUrl(fileUrl) {
  return `https://www.noteflight.com/scores/create?scoreTemplateURL=${encodeURIComponent(fileUrl)}`;
}
